import { useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { FUNNEL_ATTRIBUTES, COLORS } from "@ts/constants";
import DataChartErrorHandler from "@components/error-boundaries/DataChartErrorHandler";

interface IChartsData {
    id: string;
    name: string;
    type: string;
    data: number[];
    yAxis: string;
}
interface ICharts {
    chartsData: {
        value: {
            chartsData: IChartsData[];
        };
    };
}

interface IKey {
    activeKey: {
        value: string[];
    };
}

const DataChart = () => {
    const chartComponent = useRef(null);
    const chartsData = useSelector(
        (state: ICharts) => state.chartsData.value.chartsData
    );

    const key = [...new Set(useSelector((state: IKey) => state.activeKey.value))];
    const activeKey = key[1];
    const inactiveKey = key[0];
    /**
     * When working with nested objects or arrays in Redux, making a deep copy becomes crucial to avoid unintentional mutations.
     * If you directly modify a nested property of an object in the Redux state.
     * It can lead to unexpected behavior and make it difficult to track changes.
     */
    const options = useMemo(() => (
        JSON.parse(JSON.stringify(
            {
                chart: {
                    borderColor: "#4572A7",
                    plotBorderWidth: 1,
                    plotBorderColor: "#CCCCCC",
                    type: "spline",
                    alignTicks: false,
                    height: 300,
                },
                colors:COLORS,
                accessibility: {
                    enabled: false,
                },
                credits: {
                    enabled: false,
                },
                title: {
                    text: "",
                },
                subtitle: {
                    text: "",
                },
                time: {
                    useUTC: false,
                },
                yAxis: FUNNEL_ATTRIBUTES.map((attr) => ({
                    id: attr.key,
                    title: {
                        text: attr.name,
                    },
                    visible:
                        attr.key === activeKey || attr.key === inactiveKey
                            ? true
                            : false,
                    opposite:
                        activeKey === undefined
                            ? false
                            : attr.key === inactiveKey
                            ? true
                            : false,
    
                    lineWidth: 1,
                })),
                xAxis: {
                    type: "datetime",
                    dateTimeLabelFormats: {
                        month: "%e-%b",
                        year: "%b",
                    },
                    title: {
                        text: "Date",
                    },
                },
                legend: {
                    layout: "horizontal",
                    align: "center",
                    verticalAlign: "top",
                },
                plotOptions: {
                    areaspline: {
                        marker: {
                            enabled: true,
                        },
                        lineWidth: 3,
                    },
                },
                series: chartsData,
                responsive: {
                    rules: [
                        {
                            condition: {
                                maxWidth: 500,
                            },
                            chartOptions: {
                                legend: {
                                    layout: "horizontal",
                                    align: "center",
                                    verticalAlign: "bottom",
                                },
                            },
                        },
                    ],
                },
            }
        ))
    ), [chartsData, activeKey, inactiveKey]);

    return (
        <>
            <DataChartErrorHandler>
                <HighchartsReact
                    ref={chartComponent}
                    highcharts={Highcharts}
                    constructorType={"chart"}
                    options={options}
                    allowChartUpdate={true}
                />
            </DataChartErrorHandler>
        </>
    );
};

export default DataChart;
